import {
  changeToEnglish,
  changeToEstonian,
  changeToLabels,
  i18nNsFactory,
  LANGUAGE_ENGLISH,
  LANGUAGE_ESTONIAN,
  LANGUAGE_LABELS,
} from '@atlas-frontend/i18n';
import {
  LogoutLink,
  Tooltip,
  useConfiguration,
  useCurrentLocale,
  useUserInfo,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import { SVGAttributes, useMemo } from 'react';
import * as Icon from 'react-feather';
import { Navigate, NavLink } from 'react-router-dom';
import { path } from './paths';
import translations from './useMainSections.translations.json';
import { RouteObject, SectionObject } from './common/types';
import { Data } from './pages/Data';
import { DataDictionary } from './pages/DataDictionary';
import { NotFound } from './pages/NotFound';
import { Settings } from './pages/Settings';
import { Users } from './pages/user/Users';
import { BusinessLog } from './pages/business-log/BusinessLog';
import { Relations } from './pages/relations/Relations';
import { Dashboard } from './pages/dashboard/Dashboard';
import MySettingsIcon from './assets/img/my-settings.svg?react';
import AirflowIcon from './assets/img/airflow.svg?react';
import GrafanaIcon from './assets/img/grafana.svg?react';
import VisualValidationIcon from './assets/img/visual-validation.svg?react';
import { VisualValidation } from './pages/visual-validation/VisualValidation';
import { Tokens } from './pages/tokens/Tokens';

const { addTranslations, Translation } = i18nNsFactory('use-main-sections');
addTranslations(translations);

const SIDE_MENU_ICON_SIZE_PX = 18;

const SubMenuIcon = Object.entries(Icon).reduce<typeof Icon>(
  (submenuIcons, [IconName, Component]) => ({
    ...submenuIcons,
    [IconName]: (props: SVGAttributes<SVGElement>) => (
      <Component
        width={SIDE_MENU_ICON_SIZE_PX}
        height={SIDE_MENU_ICON_SIZE_PX}
        {...props}
      />
    ),
  }),
  {} as typeof Icon,
);

export function useMainSections() {
  const language = useCurrentLocale();
  const config = useConfiguration();
  const user = useUserInfo();

  const isUserAdmin = user.user.isAdmin || user.user.isSuperadmin;

  const sections: SectionObject[] = useMemo(
    () => [
      {
        key: 'dashboard',
        id: 'dashboard-menu-item',
        label: (
          <NavLink to="/dashboard" id="navigate-to-dashboard">
            <Translation>{(t) => t('side-menu.dashboard')}</Translation>
          </NavLink>
        ),
        icon: <Icon.Grid />,
        route: {
          path: path.dashboard + '/*',
          element: <Dashboard basePath={path.dashboard} />,
        },
      },
      {
        key: 'data-dictionary',
        id: 'data-dictionary-menu-item',
        label: (
          <NavLink to="/data-dictionary" id="navigate-to-data-dictionary">
            <Translation>{(t) => t('side-menu.data-dictionary')}</Translation>
          </NavLink>
        ),
        icon: <Icon.BookOpen />,
        route: {
          path: path.dataDictionary + '/*',
          element: <DataDictionary basePath={path.dataDictionary} />,
        },
      },
      {
        key: 'data',
        id: 'data-menu-item',
        label: (
          <NavLink to="/data" id="navigate-to-data">
            <Translation>{(t) => t('side-menu.data')}</Translation>
          </NavLink>
        ),
        icon: <Icon.Database />,
        route: {
          path: path.data + '/*',
          element: <Data basePath="/data" />,
        },
      },
      {
        key: 'relations',
        id: 'relations-menu-item',
        label: (
          <NavLink to={path.relations} id="navigate-to-relations">
            <Translation>{(t) => t('side-menu.relations')}</Translation>
          </NavLink>
        ),
        icon: <Icon.GitPullRequest />,
        route: {
          path: path.relations + '/*',
          element: <Relations basePath={path.relations} />,
        },
      },
      {
        key: 'business-log',
        id: 'business-log-menu-item',
        label: (
          <NavLink to={path.businessLog} id="navigate-to-business-log">
            <Translation>{(t) => t('side-menu.business-log')}</Translation>
          </NavLink>
        ),
        icon: <Icon.List />,
        route: {
          path: path.businessLog + '/*',
          element: <BusinessLog basePath={path.businessLog} />,
        },
      },
      {
        key: 'visual-validation',
        id: 'visual-validation-menu-item',
        label: (
          <NavLink
            to={path.visualValidation}
            id="navigate-to-visual-validation"
          >
            <Translation>{(t) => t('side-menu.visual-validation')}</Translation>
          </NavLink>
        ),
        icon: <VisualValidationIcon />,
        route: {
          path: path.visualValidation + '/*',
          element: <VisualValidation basePath={path.visualValidation} />,
        },
      },
      {
        key: 'airflow',
        id: 'airflow-menu-item',
        label: (
          <NavLink
            to={config.airflowUrl ?? ''}
            target="_blank"
            id="navigate-to-airflow"
          >
            <Translation>{(t) => t('side-menu.airflow')}</Translation>
          </NavLink>
        ),
        icon: <AirflowIcon />,
        hiddenFromMenu: !config.airflowUrl || !isUserAdmin,
      },
      {
        key: 'grafana',
        id: 'grafana-menu-item',
        label: (
          <NavLink
            to={config.grafanaUrl ?? ''}
            target="_blank"
            id="navigate-to-grafana"
          >
            <Translation>{(t) => t('side-menu.grafana')}</Translation>
          </NavLink>
        ),
        icon: <GrafanaIcon />,
        hiddenFromMenu: !config.grafanaUrl || !isUserAdmin,
      },
      {
        key: 'user-management',
        id: 'user-management-menu-item',
        label: (
          <NavLink to={path.userDirectory} id="navigate-to-users">
            <Translation>{(t) => t('side-menu.user.management')}</Translation>
          </NavLink>
        ),
        icon: <Icon.Users />,
        route: {
          path: path.userDirectory + '/*',
          element: <Users basePath={path.userDirectory} />,
        },
      },
      {
        key: 'settings',
        id: 'settings-menu-item',
        label: (
          <NavLink to={path.settings} id="navigate-to-settings">
            <Translation>{(t) => t('side-menu.settings.label')}</Translation>
          </NavLink>
        ),
        icon: <Icon.Settings />,
        route: {
          path: path.settings + '/*',
          element: <Settings basePath={path.settings} />,
        },
      },

      {
        key: 'my-settings',
        id: 'my-settings-item',
        popupClassName: 'side-menu-popup',
        icon: (
          <Tooltip
            title={
              <Translation>{(t) => t('side-menu.user.mySettings')}</Translation>
            }
          >
            <MySettingsIcon />
          </Tooltip>
        ),
        children: [
          {
            key: 'tokens',
            id: 'tokens-item',
            label: (
              <NavLink to={path.tokens} id="navigate-to-tokens">
                <Translation>{(t) => t('side-menu.user.tokens')}</Translation>
              </NavLink>
            ),
            route: {
              path: path.tokens + '/*',
              element: <Tokens basePath={path.tokens} />,
            },
          },
          {
            key: 'language',
            id: 'language-item',
            popupClassName: 'side-menu-popup',
            label: (
              <Translation>
                {(t) => t('side-menu.user.language.label')}
              </Translation>
            ),
            children: [
              {
                key: 'language-eesti',
                id: 'language-eesti-item',
                label: (
                  <Flex gap="small" align="center">
                    <Translation>
                      {(t) => t('side-menu.user.language.eesti')}
                    </Translation>
                    {(!language || language === LANGUAGE_ESTONIAN) && (
                      <Icon.Check size={20} />
                    )}
                  </Flex>
                ),
                onClick:
                  !language || language === LANGUAGE_ESTONIAN
                    ? undefined
                    : changeToEstonian,
              },
              {
                key: 'language-english',
                id: 'language-english-item',
                label: (
                  <Flex gap="small" align="center">
                    <Translation>
                      {(t) => t('side-menu.user.language.english')}
                    </Translation>
                    {language === LANGUAGE_ENGLISH && <Icon.Check size={20} />}
                  </Flex>
                ),
                onClick:
                  language === LANGUAGE_ENGLISH ? undefined : changeToEnglish,
              },
              {
                key: 'language-labels',
                id: 'language-labels-item',
                label: (
                  <Flex gap="small" align="center">
                    <Translation>
                      {(t) => t('side-menu.user.language.labels')}
                    </Translation>
                    {language === LANGUAGE_LABELS && <Icon.Check size={20} />}
                  </Flex>
                ),
                onClick:
                  language === LANGUAGE_LABELS ? undefined : changeToLabels,
              },
            ],
            icon: <SubMenuIcon.Globe />,
          },
        ],
      },

      {
        key: 'logout',
        id: 'logout-menu-item',
        label: (
          <LogoutLink id="user-menu-logout" color="white">
            <Translation>{(t) => t('side-menu.user.logout')}</Translation>
          </LogoutLink>
        ),
        icon: <Icon.LogOut />,
      },
      {
        key: 'redirect-to-dashboard',
        id: 'redirect-to-dashboard',
        route: {
          path: '/',
          element: <Navigate to="dashboard" replace />,
        },
        hiddenFromMenu: true,
      },
      {
        key: 'not-found',
        id: 'not-found-menu-item',
        hiddenFromMenu: true,
        route: {
          path: '*',
          element: <NotFound />,
        },
      },
    ],
    [config.airflowUrl, config.grafanaUrl, isUserAdmin, language],
  );

  return useMemo(() => {
    const { routes, sectionsByRoute } = sections.reduce<{
      routes: RouteObject[];
      sectionsByRoute: Map<RouteObject, SectionObject>;
    }>(
      ({ routes, sectionsByRoute }, section) => {
        collectRoutes(section, routes, sectionsByRoute);
        return { routes, sectionsByRoute };
      },
      {
        routes: [],
        sectionsByRoute: new Map(),
      },
    );
    return { sections, routes, sectionsByRoute };
  }, [sections]);
}

const collectRoutes = (
  section: SectionObject,
  routes: RouteObject[],
  sectionsByRoute: Map<RouteObject, SectionObject>,
) => {
  if (section.route) {
    routes.push(section.route);
    sectionsByRoute.set(section.route, section);
  }

  section.children?.forEach((childSection) =>
    collectRoutes(childSection, routes, sectionsByRoute),
  );
};
